import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import BlazeExpandButton from 'components/ui/BlazeExpandButton';
import styles from 'components/transactionEditor/TransactionResponseBody.module.css';
import _ from 'lodash';
import format from 'xml-formatter';
import ResponseNodeWidget from './Wizards/ResponseNodeWidget';
import { RequestTabState } from '../../views/AssetCatalog/Transactions/TransactionEditor/RequestTab/RequestTab';
import { getJsonObject, isJsonString } from 'utils/jsonUtil';
import { isXmlString } from 'utils/xmlUtil';
import clsx from 'clsx';
import RadioButton from '../buttons/RadioButton';
import { createAiAssistantIcon } from '../../views/AssetCatalog/AiAssistant/aiAssistantUtil';
import BzmIcon from '../icons/BzmIcon';
import AiAssistantModal from '../../views/AssetCatalog/AiAssistant/AiAssistantModal';
import { getAccountAiConsent } from '../../utils/bzmUserConfigUtil';
import ResponseBodyEditor from './ResponseBodyEditor';
import { EditorView } from '@codemirror/view';
import { BodyValidationError } from 'types/rest/bodyValidationTypes';
import { getEditorLanguage } from 'utils/bodyEditorUtil';
import axios from 'axios/axios-base';

interface ResponseBodyProps {
  content: string;
  binary: boolean;
  onChange: (field: string, value: any) => void;
  isReadOnly?: boolean;
  isResponseBodyMagicStringInvalid?: boolean;
  resBodyInvalidMagicStrings?: string;
  requestTabState: RequestTabState;
  serviceId?: number;
  hideWidgets?: boolean;
  hideContentType?: boolean;
  checkInvalidContent?: boolean;
  setCheckInvalidContent?: (val: boolean) => void;
  enableAiAssistant?: boolean; // Flag to enable AI assistant functionality
  dispatch?: Dispatch; // Redux dispatch for clearing conversation
}

/**
 * Simple holder which keeps information about the $placeholder variable
 * and its type. The type cannot be interfered during string interpolation (everything is string)
 * so that is the reason why we keep key / type information separately.
 */
export interface BodyTemplateTypes {
  [key: string]: 'string' | 'bool';
}

class ResponseBody extends React.Component<ResponseBodyProps> {
  state = {
    isTextAreaExpanded: false,
    textAreaHeightInCols: 19,
    buttonText: i18n.t('transactions:editor.showMore'),
    binary: this.props.binary,
    bodyContent: this.props.content,
    identifiedBodyContentType: 'text',
    indexOfNodes: -1,
    bodyContentTemplate: '',
    bodyContentTemplateTypes: {} as BodyTemplateTypes,
    hideContentType: this.props.hideContentType,
    validateEmptyBody: false,
    // Validation state
    validationErrors: [] as BodyValidationError[],
    isValidating: false,
    validationSuccess: false,
    // AI Assistant state
    showAiAssistant: false,
    aiAssistantCallerId: '',
    aiSelectionRange: undefined as { start: number; end: number } | undefined,
  };

  componentDidMount() {
    this.parseContent(this.state.bodyContent);
  }

  componentDidUpdate(prevProps: Readonly<ResponseBodyProps>, prevState: any) {
    if (!_.isEqual(prevProps.content, this.props.content)) {
      this.setState({ bodyContent: this.props.content });
      this.parseContent(this.props.content);
    }
    if (!_.isEqual(prevProps.binary, this.props.binary)) {
      this.setState({ binary: this.props.binary });
    }
    if (!_.isEqual(prevProps.hideContentType, this.props.hideContentType)) {
      this.setState({ hideContentType: this.props.hideContentType });
    }
    if (!prevState.showAiAssistant && this.state.showAiAssistant && this.state.aiSelectionRange) {
      this.editorView?.focus();
    }
  }

  instance = null;
  editorView: EditorView | null = null;

  setBodyEditorClass = () => {
    const newHeightInCols: number = !this.state.isTextAreaExpanded ? 38 : 19;
    const buttonText: string = !this.state.isTextAreaExpanded
      ? i18n.t('transactions:editor.showLess')
      : i18n.t('transactions:editor.showMore');

    this.setState({
      isTextAreaExpanded: !this.state.isTextAreaExpanded,
      textAreaHeightInCols: newHeightInCols,
      buttonText: buttonText,
    });
  };

  handleChange = (name: string, valueArg: any) => {
    this.setState({ bodyContent: valueArg, validationErrors: [], validationSuccess: false });
    this.props.onChange(name, valueArg);
    this.formatContent(valueArg);
  };

  handleValidate = () => {
    this.setState({ isValidating: true, validationErrors: [], validationSuccess: false });
    axios
      .post('/system/validations/handlebars/blazemeter', { template: this.state.bodyContent })
      .then(response => {
        const result = response.data.result;
        const errors: BodyValidationError[] = result.valid
          ? []
          : [
              {
                line: result.lineNumber ?? 1,
                column: 1,
                endLine: result.lineNumber ?? 1,
                endColumn: 1,
                message: result.message,
                severity: 'error' as const,
              },
            ];
        this.setState({ validationErrors: errors, validationSuccess: result.valid });
      })
      .catch(() => {
        this.setState({
          validationErrors: [
            {
              line: 1,
              column: 1,
              endLine: 1,
              endColumn: 1,
              message: 'Validation failed. Please try again.',
              severity: 'error' as const,
            },
          ],
          validationSuccess: false,
        });
      })
      .finally(() => {
        this.setState({ isValidating: false });
      });
  };

  toggleBinary = () => {
    this.props.onChange('binary', !this.props.binary);
  };

  formatContent = _.debounce((valueArg: any) => {
    if (!this.props.binary) {
      this.parseContent(valueArg);
    } else {
      this.setState({
        identifiedBodyContentType: 'text',
      });
    }
  }, 1000);

  prettifyContent = (content: string) => {
    if (this.state.identifiedBodyContentType === 'json') {
      const contentObj = getJsonObject(content);
      const formattedJson = JSON.stringify(contentObj, null, 4);
      this.parseJsonAndCreateTemplate(contentObj);
      this.setState({
        bodyContent: formattedJson,
      });
    } else if (this.state.identifiedBodyContentType === 'xml') {
      const formattedXml = format(content);
      this.parseXMLAndCreateTemplate(formattedXml);
      this.setState({
        bodyContent: formattedXml,
      });
    }
  };

  parseContent = (content: string) => {
    if (content) {
      // Check if content is json
      if (isJsonString(content)) {
        this.setState({
          identifiedBodyContentType: 'json',
        });
        const contentObj = getJsonObject(content);
        this.parseJsonAndCreateTemplate(contentObj);
      } else {
        // Check if content is XML
        if (isXmlString(content)) {
          this.setState({
            identifiedBodyContentType: 'xml',
          });
          this.parseXMLAndCreateTemplate(content);
        } else {
          this.setState({ identifiedBodyContentType: 'text' });
        }
      }
    } else {
      this.setState({ identifiedBodyContentType: 'text' });
    }
  };

  parseXMLAndCreateTemplate = (content: string) => {
    const domObj = new DOMParser().parseFromString(content, 'text/xml');
    this.removeSpaces(domObj.getRootNode());
    const list = [] as string[];
    this.loopNodes(domObj.getRootNode().childNodes, list);
    this.setState({ bodyContentTemplate: new XMLSerializer().serializeToString(domObj) });
  };

  parseJsonAndCreateTemplate = (jsonObj: any) => {
    const jsonKeys = [] as string[];
    this.json_getAllKeys(jsonObj, jsonKeys, false);
    const types = {};
    const templateObj = this.loopJsonNodes(jsonObj, [], jsonKeys, types);
    this.setState({ bodyContentTemplate: JSON.stringify(templateObj), bodyContentTemplateTypes: types });
  };

  /**
   * Goes through json object and replaces values with template placeholders which can be later substituted.
   * @param jsonObj
   * @param list
   * @param keys
   */
  loopJsonNodes = (jsonObj: any, list: string[], keys: string[], types: BodyTemplateTypes) => {
    try {
      //Go through all properties in json object
      for (const i in jsonObj) {
        //Nested object, process recursively
        if (typeof jsonObj[i] === 'object') {
          list.unshift(i);
          const clonedList = [...list];
          if (this.isArray(jsonObj[i]) && this.checkIfArrayOfLiterals(jsonObj[i], clonedList, list.length)) {
            jsonObj[i] = '#' + i + '_' + list.length;
          } else if (!this.checkIfJsonHasKey(keys, i)) {
            list.shift();
            this.loopJsonNodes(jsonObj[i], list, keys, types);
          } else {
            this.loopJsonNodes(jsonObj[i], list, keys, types);
          }
        }
        // Process value
        else {
          list.unshift(i);
          const currentValue = jsonObj[i];

          //Check the type of the value being processed
          let valueType = 'string';
          if (_.isBoolean(currentValue)) {
            valueType = 'bool';
          }

          //Replace the value in the original object with #placeholder
          const valuePlaceholder = '#' + i + '_' + list.length;
          jsonObj[i] = valuePlaceholder;
          types[valuePlaceholder] = valueType as any;
        }
      }
    } catch (e) {
      console.error('Error while trying to loop json nodes.');
    }
    return jsonObj;
  };

  checkIfJsonHasKey = (keys: string[], key: string) => {
    return keys.includes(key);
  };

  /**
   * Gets all keys in the json object as simple array list of strings.
   * @param jsonObj
   * @param keys
   * @param skipNext
   */
  json_getAllKeys = (jsonObj: any, keys: string[], skipNext: boolean) => {
    try {
      for (const i in jsonObj) {
        if (typeof jsonObj[i] === 'object') {
          if (!skipNext) {
            keys.unshift(i);
          }
          if (Array.isArray(jsonObj[i])) {
            this.json_getAllKeys(jsonObj[i], keys, true);
          } else if (jsonObj[i] === null) {
            keys.shift();
          } else {
            this.json_getAllKeys(jsonObj[i], keys, false);
          }
        } else {
          if (!skipNext) {
            keys.unshift(i);
          }
        }
      }
    } catch (e) {
      console.error('Error while trying to find json keys.');
    }
  };

  isArray = (jsonObj: any) => {
    try {
      return jsonObj && jsonObj.length > 1;
    } catch (e) {
      console.error('Error while trying to find if object is array.');
    }
  };

  checkIfArrayOfLiterals = (jsonObj: any, list: string[], originalLength: number) => {
    try {
      for (const i in jsonObj) {
        if (list.length !== originalLength) {
          return false;
        }

        if (typeof jsonObj[i] === 'object') {
          list.unshift(i);
          this.checkIfArrayOfLiterals(jsonObj[i], list, originalLength);
        }
      }
      return true;
    } catch (e) {
      console.error('Error while trying to check if its an array of literals.');
    }
  };

  loopNodes = (nodeList: NodeListOf<Node>, list: string[]) => {
    try {
      for (let i = 0; i < nodeList.length; i++) {
        this.removeSpaces(nodeList.item(i));
        this.setState({ indexOfNodes: this.state.indexOfNodes + 1 });
        if (
          nodeList.item(i).childNodes.length === 1 &&
          (nodeList.item(i).childNodes.item(0).nodeType === 3 || nodeList.item(i).childNodes.item(0).nodeType === 4)
        ) {
          list.unshift(nodeList.item(i).nodeName);
          const textNode = document.createTextNode('#' + nodeList.item(i).nodeName + '_' + list.length);
          nodeList.item(i).replaceChild(textNode, nodeList.item(i).childNodes.item(0));
        } else {
          if (nodeList.item(i).childNodes.length > 0) {
            list.unshift(nodeList.item(i).nodeName);
          }
          this.loopNodes(nodeList.item(i).childNodes, list);
        }
      }
    } catch (e) {
      console.error('Error while trying to loop xml nodes.');
    }
  };

  removeSpaces = (parentNode: Node) => {
    try {
      for (let i = 0; i < parentNode.childNodes.length; i++) {
        if (parentNode.childNodes.item(i).nodeType === 3) {
          if (
            parentNode.childNodes.item(i).nodeValue !== null &&
            this.getNodeValue(parentNode.childNodes.item(i)).length === 0
          ) {
            parentNode.removeChild(parentNode.childNodes.item(i));
          }
        }
      }
    } catch (e) {
      console.error('Error while trying to remove spaces in xml nodes.');
    }
  };

  getNodeValue = (node: ChildNode) => {
    return node && node.nodeValue !== null ? node.nodeValue.trim() : '';
  };

  updateBodyContent = (content: string) => {
    this.setState({ bodyContent: content });
    this.props.onChange('content', content);
    this.parseContent(content);
    this.prettifyContent(content);
  };

  handleEditorView = (view: EditorView | null) => {
    this.editorView = view;
  };

  // AI Assistant handlers
  handleAiAssistantShow = () => {
    const sel = this.editorView?.state.selection.main;
    const aiSelectionRange = sel && sel.from !== sel.to ? { start: sel.from, end: sel.to } : undefined;
    this.setState({
      showAiAssistant: true,
      aiAssistantCallerId: 'qa-textarea-body-content',
      aiSelectionRange,
    });
  };

  handleAiAssistantUpdate = (value: string) => {
    this.setState({ bodyContent: value });
    this.props.onChange('content', value);
    this.parseContent(value);
  };

  handleAiAssistantClose = () => {
    this.setState({ showAiAssistant: false, aiSelectionRange: undefined });
  };

  // Check if AI icon should be shown
  shouldShowAiIcon = () => {
    return this.props.enableAiAssistant && !this.props.isReadOnly && getAccountAiConsent();
  };

  render() {
    const { isTextAreaExpanded, textAreaHeightInCols, buttonText } = this.state;
    const isEmptyContent = this.state.bodyContent.trim().length < 1;
    return (
      <>
        {this.state.hideContentType !== true && (
          <div className={'mb-4 d-flex align-items-center'} style={{ minHeight: 30 }}>
            <div className="mr-3">
              <RadioButton
                isChecked={!this.props.binary}
                id={'response-type-raw'}
                label={i18n.t('transactions:editor.rawLabel')}
                onChange={this.toggleBinary}
              ></RadioButton>
            </div>

            <RadioButton
              isChecked={!!this.props.binary}
              id={'response-type-binary'}
              label={i18n.t('transactions:editor.binaryLabel')}
              onChange={this.toggleBinary}
            ></RadioButton>
          </div>
        )}
        {this.state.hideContentType !== true && <Form.Label>{i18n.t('transactions:editor.bodyTitle')}</Form.Label>}
        <div className="float-right">
          {this.props.binary && (
            <span className="divided divided-gap-lg">{i18n.t('transactions:editor.binaryDescription')}</span>
          )}
          <span className="divided divided-gap-lg">
            {((this.props.hideWidgets !== true &&
              !this.props.binary &&
              this.state.identifiedBodyContentType === 'json') ||
              this.state.identifiedBodyContentType === 'xml') && (
              <ResponseNodeWidget
                type={this.state.identifiedBodyContentType}
                responseBody={this.state.bodyContent}
                responseBodyTemplate={{
                  body: this.state.bodyContentTemplate,
                  types: this.state.bodyContentTemplateTypes,
                }}
                requestTabState={this.props.requestTabState}
                updateBodyContent={this.updateBodyContent}
                prettifyContent={this.prettifyContent}
                serviceId={this.props.serviceId}
              />
            )}
            {this.state.validationSuccess && <BzmIcon variant={'icon-bzm-success'} className="text-success mr-1" />}
            <button
              type="button"
              className="btn btn-link btn-no-hover mb-1 mt-0 pb-2 pt-0"
              title={'Validate'}
              onClick={this.handleValidate}
              disabled={this.state.isValidating}
            >
              <span className={'font-size-base font-weight-semi-bold text-primary'}>
                {this.state.isValidating ? 'Validating...' : 'Validate'}
              </span>
            </button>
            <div className="vdivider-response-wizard ml-2 mr-2"></div>
            <button
              type="button"
              className="btn btn-link btn-no-hover mb-1 mt-0 pb-2 pt-0"
              title={'AI Assistant'}
              onClick={this.handleAiAssistantShow}
            >
              {createAiAssistantIcon()}
              <span className={'ml-3 font-size-base font-weight-semi-bold text-primary'}>AI Assistant</span>
            </button>
            <div className="vdivider-response-wizard ml-2 mr-2"></div>
            <BlazeExpandButton
              isTextAreaExpanded={isTextAreaExpanded}
              buttonClass={clsx('p-0', 'mb-2', 'font-weight-semi-bold', styles.linkMinWidth)}
              buttonText={buttonText}
              fnCallback={this.setBodyEditorClass}
            />
          </span>
        </div>

        <ResponseBodyEditor
          value={this.state.bodyContent}
          language={getEditorLanguage(this.state.bodyContent, this.state.identifiedBodyContentType)}
          readOnly={!!this.props.isReadOnly}
          height={textAreaHeightInCols * 19}
          validationErrors={this.state.validationErrors}
          onValidationErrorsDismiss={() => this.setState({ validationErrors: [] })}
          onChange={value => this.handleChange('content', value)}
          onBlur={() => (this.props.setCheckInvalidContent ? this.props.setCheckInvalidContent(true) : null)}
          isInvalid={
            (!!this.props.isResponseBodyMagicStringInvalid && !this.props.binary) ||
            (!!this.props.checkInvalidContent && isEmptyContent)
          }
          onEditorView={this.handleEditorView}
        />

        {!this.props.binary && this.props.isResponseBodyMagicStringInvalid && this.props.resBodyInvalidMagicStrings && (
          <div className="invalid-feedback d-block">{this.props.resBodyInvalidMagicStrings} syntax is incorrect.</div>
        )}
        {this.props.checkInvalidContent && isEmptyContent && (
          <div className="invalid-feedback d-block">Body is required</div>
        )}

        {/* AI Assistant Modal */}
        {this.state.showAiAssistant && (
          <AiAssistantModal
            callerId={this.state.aiAssistantCallerId}
            updateFunction={this.handleAiAssistantUpdate}
            currentValue={this.state.bodyContent}
            onClose={this.handleAiAssistantClose}
            title="Response Body"
            selectionRange={this.state.aiSelectionRange}
          />
        )}
      </>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatch,
});

export default connect(null, mapDispatchToProps)(ResponseBody);
